import type { SupabaseClient } from "@supabase/supabase-js";
import { withBase } from "./base";
import { ensureParentProfile } from "./auth";

/**
 * Joining, signing in, and where an account stands.
 *
 * The account is the same Al Noor account the LMS uses — one project, one
 * cookie, one identity. What is new is that a family never has to leave
 * /homeschool to get one.
 */

export type AccountState = {
  /** Whether a new day may be started. The database enforces this too. */
  access: boolean;
  status: "trialing" | "active" | "past_due" | "canceled" | "expired" | "granted" | "none" | "unknown";
  plan?: string | null;
  currency?: string | null;
  endsAt?: string | null;
  /** Whole days remaining, rounded up, so the last day reads "1 day left". */
  daysLeft?: number | null;
};

/** Re-evaluate expiry without waiting for another network request. */
export function accountAtTime(account: AccountState, now = Date.now()): AccountState {
  if (account.status !== "active" && account.status !== "trialing") return account;
  const end = account.endsAt ? Date.parse(account.endsAt) : null;
  return {
    ...account,
    access: end === null || (Number.isFinite(end) && end > now),
    daysLeft: end !== null && Number.isFinite(end) ? Math.max(0, Math.ceil((end - now) / 86_400_000)) : null,
  };
}

export type Plan = {
  code: string;
  title: string;
  description: string | null;
  price_usd: number | null;
  price_pkr: number | null;
  billing_interval: "month" | "year" | null;
};

/**
 * The introductory price, and how many places are left on it.
 *
 * Counted in SQL by homeschool_intro_offer, because RLS lets a family read
 * their own subscription and nothing else — the client cannot count. The
 * function returns totals only, never rows, so the page learns how many
 * families subscribed and nothing about who they are.
 */
export type IntroOffer = {
  code: string;
  intro_price_usd: number | null;
  intro_price_pkr: number | null;
  intro_limit: number | null;
  taken: number;
  remaining: number;
  is_open: boolean;
};

export async function fetchIntroOffer(supabase: SupabaseClient): Promise<Map<string, IntroOffer>> {
  const byCode = new Map<string, IntroOffer>();
  try {
    const { data, error } = await supabase.rpc("homeschool_intro_offer");
    if (error || !Array.isArray(data)) return byCode;
    for (const row of data as IntroOffer[]) byCode.set(row.code, row);
  } catch {
    // No offer configured, or the migration has not run. The page then shows
    // the standard price, which is true and is the safe thing to be wrong
    // about — never the reverse.
  }
  return byCode;
}

/** What a plan costs right now, honouring an open introductory offer. */
export function effectivePrice(
  plan: Plan,
  currency: "USD" | "PKR",
  offer?: IntroOffer,
): { amount: number | null; wasAmount: number | null } {
  const standard = currency === "PKR" ? plan.price_pkr : plan.price_usd;
  if (!offer?.is_open) return { amount: standard, wasAmount: null };
  const intro = currency === "PKR" ? offer.intro_price_pkr : offer.intro_price_usd;
  if (intro == null) return { amount: standard, wasAmount: null };
  return { amount: intro, wasAmount: standard };
}

/** Format a bare amount the same way formatPrice does, for intro prices. */
export function formatAmount(amount: number | null, currency: "USD" | "PKR"): string {
  if (amount == null) return "";
  return currency === "PKR"
    ? `PKR ${Number(amount).toLocaleString("en-PK")}`
    : `$${Number(amount).toFixed(Number(amount) % 1 === 0 ? 0 : 2)}`;
}

/**
 * Supabase's error, as a key the sign-in card can say in either language.
 *
 * Matched on the stable `code` first and the English message second, because
 * older auth servers send only the message.
 */
export type AuthProblem = "exists" | "rateLimited" | "weakPassword" | "badEmail" | "wrongPassword" | "notConfirmed" | "unknown";

function problemOf(error: { message: string; code?: string }): AuthProblem {
  const code = error.code ?? "";
  const message = error.message.toLowerCase();
  if (code === "user_already_exists" || code === "email_exists" || message.includes("already registered")) return "exists";
  if (code.startsWith("over_") || message.includes("rate limit")) return "rateLimited";
  if (code === "weak_password" || message.includes("password should")) return "weakPassword";
  if (code === "email_address_invalid" || code === "validation_failed" || message.includes("invalid format")) return "badEmail";
  if (code === "invalid_credentials" || message.includes("invalid login")) return "wrongPassword";
  if (code === "email_not_confirmed" || message.includes("not confirmed")) return "notConfirmed";
  return "unknown";
}

export type AuthResult =
  | { ok: true; signedIn: true }
  | { ok: true; checkEmail: true }
  | { ok: false; problem: AuthProblem; detail: string };

/**
 * Open the account and walk straight in.
 *
 * ── What was wrong ──────────────────────────────────────────────────────────
 * "Confirm email" is switched off on the Supabase project, so signUp signs the
 * family in on the spot and sends nothing. This function used to ignore that
 * session and tell them to check their email — for a confirmation that was
 * never going to be sent — while the only thing that would have created their
 * parent profile was the link in it. They were in fact signed in, to an
 * account with no profile, and the app turned them away at the door every time
 * they came back. Four of five homeschool sign-ups ended there.
 *
 * Now the session is used: the profile is made at once (see
 * ensureParentProfile) and the family goes straight to choosing a curriculum.
 *
 * ── If confirmation is ever switched back on ────────────────────────────────
 * Then Supabase returns no session and sends the mail itself, through the
 * main site's send-email hook, and saying "check your email" is true again —
 * so that answer stays as the fallback. The confirm link already lands back
 * here and makes the profile from `requested_role`.
 */
export async function register(
  supabase: SupabaseClient,
  input: { name: string; email: string; password: string },
): Promise<AuthResult> {
  const { data, error } = await supabase.auth.signUp({
    email: input.email.trim(),
    password: input.password,
    options: {
      // `requested_role` is the key the confirm route reads. `role` — which the
      // LMS sends — is ignored there and would land them a student profile.
      data: { full_name: input.name.trim(), requested_role: "parent", source: "homeschool" },
      // Only used when confirmation is required (see above): back to
      // /homeschool, not to the LMS.
      emailRedirectTo: `${window.location.origin}/auth/confirm?next=${encodeURIComponent(withBase("/"))}`,
    },
  });

  if (error) return { ok: false, problem: problemOf(error), detail: error.message };

  if (data.session) {
    // Not fatal if it fails: the boot that follows asks again, and that second
    // chance is the one that matters.
    await ensureParentProfile(data.session.access_token);
    return { ok: true, signedIn: true };
  }

  // No session and no error. With confirmation on, that is a new account
  // waiting for its email — or an address that already had one, which
  // Supabase will not say aloud. "Check your email" is right for both.
  return { ok: true, checkEmail: true };
}

export async function signIn(
  supabase: SupabaseClient,
  input: { email: string; password: string },
): Promise<AuthResult> {
  const { data, error } = await supabase.auth.signInWithPassword({ email: input.email.trim(), password: input.password });
  if (error) return { ok: false, problem: problemOf(error), detail: error.message };
  // A family who joined before the fix has an account with no profile. Signing
  // in is the moment they come back, so finish it now.
  if (data.session) await ensureParentProfile(data.session.access_token);
  return { ok: true, signedIn: true };
}

/**
 * Send a reset link, landing back here.
 *
 * The redirect is a request, not a promise: Supabase's allow-list holds only
 * the main site's callback, so it may substitute its own site URL — in which
 * case the main site's send-email hook sends a homeschool account to
 * /homeschool/ anyway. Either way the family lands here with a recovery
 * session, and NewPasswordPrompt asks for the new password.
 *
 * Reports success whatever the address, like every reset form should: whether
 * an account exists is nobody's business but its owner's.
 */
export async function sendPasswordReset(
  supabase: SupabaseClient,
  email: string,
): Promise<{ ok: true } | { ok: false; problem: AuthProblem; detail: string }> {
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${window.location.origin}${withBase("/")}`,
  });
  if (error && problemOf(error) === "rateLimited") return { ok: false, problem: "rateLimited", detail: error.message };
  return { ok: true };
}

/**
 * What the trial banner and the paywall read.
 *
 * Read directly rather than through the Worker: a family may read their own
 * subscription under RLS, so this needs no service-role key anywhere — which is
 * the whole reason the homeschool Worker no longer holds one.
 *
 * homeschool_ensure_trial starts the free week if this account has never had a
 * row. It is a security-definer function that can only ever write ONE row, for
 * the caller, and only ever a trial; it never re-arms an expired one. Calling
 * it here also covers a family who arrived through some other Al Noor sign-up
 * path and would otherwise have had no entitlement record at all.
 *
 * Never throws. A billing outage must not become an outage.
 */
export async function fetchAccountState(supabase: SupabaseClient): Promise<AccountState> {
  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session.session) return { access: true, status: "unknown" };

    const { data: row, error } = await supabase.rpc("homeschool_ensure_trial").single();
    if (error || !row) return { access: true, status: "unknown" };

    const record = row as {
      status: AccountState["status"];
      plan_code: string | null;
      currency: string | null;
      trial_ends_at: string | null;
      current_period_end: string | null;
    };
    const now = Date.now();
    const endsAt = record.status === "trialing" ? record.trial_ends_at : record.current_period_end;
    // Mirrors public.homeschool_has_access, which is what actually enforces it.
    // If the two ever disagree the database wins, so the screen is merely wrong
    // rather than the paywall being bypassable.
    const access =
      record.status === "granted" ||
      record.status === "past_due" ||
      ((record.status === "active" || record.status === "trialing") &&
        (!endsAt || Date.parse(endsAt) > now));
    return {
      access,
      status: record.status,
      plan: record.plan_code,
      currency: record.currency,
      endsAt,
      daysLeft: endsAt ? Math.max(0, Math.ceil((Date.parse(endsAt) - now) / 86_400_000)) : null,
    };
  } catch {
    return { access: true, status: "unknown" };
  }
}

/**
 * The plans, readable signed out.
 *
 * The price belongs on the page before anyone makes an account — a price a
 * visitor has to register to discover costs sign-ups.
 */
export async function fetchPlans(supabase: SupabaseClient): Promise<Plan[]> {
  try {
    const { data, error } = await supabase
      .from("homeschool_products")
      .select("code,title,description,price_usd,price_pkr,billing_interval")
      .eq("kind", "plan")
      .eq("is_active", true)
      .order("sort_order");
    if (error || !data) return [];
    return data as Plan[];
  } catch {
    return [];
  }
}

/**
 * Which currency to quote.
 *
 * Pakistan Standard Time is the signal, not the browser language: a Pakistani
 * family very often has an English-language phone, and quoting them dollars
 * would be both wrong and slightly insulting. Falls back to USD, which is the
 * safer thing to be wrong about — a visitor shown a dollar price abroad
 * understands it, and one shown rupees abroad does not.
 */
export function preferredCurrency(): "USD" | "PKR" {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Karachi" ? "PKR" : "USD";
  } catch {
    return "USD";
  }
}

export function formatPrice(plan: Plan, currency: "USD" | "PKR"): string {
  const amount = currency === "PKR" ? plan.price_pkr : plan.price_usd;
  if (amount == null) return "";
  return currency === "PKR"
    ? `PKR ${Number(amount).toLocaleString("en-PK")}`
    : `$${Number(amount).toFixed(Number(amount) % 1 === 0 ? 0 : 2)}`;
}

/**
 * What the yearly plan saves against paying monthly, in whole free months.
 *
 * Computed rather than written into the copy, so a price change cannot leave a
 * claim behind that is no longer true. A number rather than a sentence, so the
 * page can say it in Urdu too: an English "2 months free" inside an Urdu line
 * came out reordered by the right-to-left text as "months free 2".
 */
export function annualSaving(
  plans: Plan[],
  currency: "USD" | "PKR",
  offers?: Map<string, IntroOffer>,
): number | null {
  const monthly = plans.find((plan) => plan.billing_interval === "month");
  const annual = plans.find((plan) => plan.billing_interval === "year");
  if (!monthly || !annual) return null;
  // Measured against whatever a family would actually pay today. Comparing an
  // introductory yearly price with a standard monthly one would overstate the
  // saving, which is the one direction this must never be wrong in.
  const monthlyPrice = effectivePrice(monthly, currency, offers?.get(monthly.code)).amount;
  const annualPrice = effectivePrice(annual, currency, offers?.get(annual.code)).amount;
  if (monthlyPrice == null || annualPrice == null || monthlyPrice <= 0) return null;
  const monthsPaid = Number(annualPrice) / Number(monthlyPrice);
  const saved = 12 - monthsPaid;
  if (saved < 0.5) return null;
  return Math.round(saved);
}
